import { ActionType } from '../../../model/client-model/properties/action-type';
import { Stakeholder, ToApiStakeholder } from '../../../model/stakeholder';
import { STAKEHOLDER_TYPE } from '../../backend-service/model';
import { Swimlane } from '../../backend-service/model/board-settings-v2';
import { ActionOrigin } from '../../../model/client-model/properties/action-origin';

type ItemId = string;

/**
 * Find the item that was moved to anoder index position in the new order
 * Assumes only ordering of items has changed, no items have been created, updated or deleted
 * @param oldItemsOrder The original order of items
 * @param newItemsOrder The new order of items
 * @returns The id of the moved item, if any
 */
export function findMovedItemId(oldItemsOrder: ItemId[], newItemsOrder: ItemId[]): ItemId | undefined {
  const oldIndexMap = new Map(oldItemsOrder.map((id, index) => [id, index]));

  // Calculate the difference in index for each item in the new order compared to the old order
  const indexDifferences = newItemsOrder.map((id, newIndex) => {
    const oldIndex = <number>oldIndexMap.get(id);
    return { id, difference: Math.abs(newIndex - oldIndex) };
  });

  // Sort items by the absolute difference in their indices, descending
  indexDifferences.sort((a, b) => b.difference - a.difference);

  /**
   * If: The largest difference is 0
   *   No item was moved
   *
   * Else If: the largest difference is 1
   *   The item moved was either the first or second item in the sorted order.
   *   In that case, we can return either of them.
   *
   * Else (diffeerence > 1):
   *   The item moved was the one with the largest difference in index
   */
  const noItemMoved = indexDifferences[0].difference === 0;
  if (noItemMoved) {
    return undefined;
  }
  const movedItem = indexDifferences[0];
  return movedItem.id;
}

export function distinguishChangedItems<T extends Swimlane | Stakeholder | ActionType | ActionOrigin>(
  items: T[],
  oldItems: T[]
): { created: T[]; updated: T[]; deleted: T[] } {
  const created = items.filter((item) => {
    return !oldItems.some((oldItem) => oldItem.id === item.id);
  });
  const updated = items.filter((item) => {
    const oldItem = oldItems.find((oldItem) => oldItem.id === item.id);
    if (!oldItem) {
      return false;
    }

    // Handle ActionOrigin objects with title and description
    if ('title' in item && 'title' in oldItem) {
      const titleChanged = oldItem.title !== item.title;
      const typeChanged = oldItem.type !== item.type;

      // Check for description changes, properly handling undefined/null values
      const descriptionChanged =
        ('description' in item || 'description' in oldItem) &&
        (item as ActionOrigin)['description'] !== (oldItem as ActionOrigin)['description'];

      return titleChanged || typeChanged || descriptionChanged;
    } else if ('name' in oldItem && 'name' in item) {
      return oldItem.name !== item.name || oldItem.type !== item.type;
    } else if ('displayValue' in oldItem && 'displayValue' in item) {
      return oldItem.displayValue !== item.displayValue;
    }

    return false;
  });
  const deleted = oldItems.filter((oldItem) => {
    return !items.some((item) => item.id === oldItem.id);
  });

  return { created, updated, deleted };
}

export function isValidStakeholder(stakeholder: Stakeholder): stakeholder is ToApiStakeholder {
  return stakeholder.type !== STAKEHOLDER_TYPE.NOTSET;
}

export function validateCreatedAndUpdatedStakeholders(
  createdStakeholders: Stakeholder[],
  updatedStakeholders: Stakeholder[]
): { validCreatedStakeholders: ToApiStakeholder[]; validUpdatedStakeholders: ToApiStakeholder[] } {
  const { errorIds: createdErrors, valid: validCreated } = _validateStakeholdersByType(createdStakeholders);
  const { errorIds: updatedErrors, valid: validUpdated } = _validateStakeholdersByType(updatedStakeholders);

  const allErrorIds = [...createdErrors, ...updatedErrors];
  if (allErrorIds.length) {
    throw new Error(`Stakeholder type is not set for stakeholder id:\n${allErrorIds.map((id) => id).join('\n')}`);
  }

  return { validCreatedStakeholders: validCreated, validUpdatedStakeholders: validUpdated };
}

function _validateStakeholdersByType(stakeholders: Stakeholder[]): {
  errorIds: string[];
  valid: ToApiStakeholder[];
} {
  return stakeholders.reduce(
    (obj: { errorIds: string[]; valid: ToApiStakeholder[] }, s) => {
      isValidStakeholder(s) ? obj.valid.push(s) : obj.errorIds.push(s.id);
      return obj;
    },
    { errorIds: [], valid: [] }
  );
}
